import { useState, useEffect, useRef } from 'react';
import styles from './SentenceBlock.module.css';
import common from '../Common.module.css';
import { useSentenceGameLogic } from './useSentenceGameLogic';
import ResetButton from './ResetButton';
import HintButton from './HintButton';
import { useAppConfig } from '../../../context/AppConfigContext';
import { useAssistant } from '../../../components/Assistant/AssistantContext'; 
import { getAssistantGrammarHints } from '../../../hooks/useAssistantHints';
import { Capacitor } from '@capacitor/core';

const SentenceBlock = ({
  block,
  onComplete,
  mode = 'sequential',
  previousScore,
  userId,
  allowGrammarHints = false,
}) => {
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const { t, baseLanguage, targetLanguage, isAndroidApp, site } = useAppConfig();
  const isNative = Capacitor.isNativePlatform();
  const combinedIsAndroidApp = isAndroidApp || isNative; 
  
  const { actions, state } = useAssistant();
  const { sentences, activeSentences, completedSentences, sentencesPerRound, slots, availableWords, wordStyles, showCorrect, errorMessages, fadingSentences, score, draggedWord, hoveredSlot, slotsRef, wordsRef, containerRef, checkAnswer, moveToNextSentence, resetSentence, hintWord, handleDragStart, handleDragOver, handleDragLeave, handleDrop, handleSlotClick, handleTouchStart, handleTouchEnd, handleDrag, dragPositionRef, isDraggingDisabled } = useSentenceGameLogic({ block, mode, currentSentenceIndex, setCurrentSentenceIndex, onComplete, previousScore, actions, combinedIsAndroidApp, site });
  
  //console.log(`Внутри SentenceBlock. Мне надо тригер для срабатывания ассистента, например, currentSentenceIndex ${currentSentenceIndex} + mode`);

  useEffect(() => {
    actions.showSuccess();

    if (!allowGrammarHints) return;

    getAssistantGrammarHints({
      userId,
      language: baseLanguage,
      targetLanguage,
      actions,
      source: 'pathLessons',
    });
  }, [currentSentenceIndex, allowGrammarHints, userId, baseLanguage, targetLanguage, actions]);

  return (
    <div className={mode === 'sequential' ? styles.container : styles.containerCompact} ref={containerRef} >
	
		{/* Заголовок и вступительная часть: очки + количество фраз */}
		{mode === 'sequential' ? (
		  <>
			<h1 className={styles.sentenceTitle}>
			  {t.translateBlock.title.replace('{blockTitle}', block.blockTitle)} (
				{currentSentenceIndex + 1}/{sentences.length || 10}
			  )
			</h1>
			<p className={styles.score}>
			  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="gold" style={{ marginRight: '6px', verticalAlign: 'middle' }} >
				<path d="M12 .587l3.668 7.568L24 9.423l-6 5.885 1.417 8.255L12 18.897l-7.417 4.666L6 15.308 0 9.423l8.332-1.268z" />
			  </svg>
			  {previousScore + score}
			</p>
		  </>
		) : (
			<h1 className={styles.headerCompact}>
			  {Math.min(completedSentences + 1, sentencesPerRound)}/{sentencesPerRound || 10}
			  <span className={styles.scoreCompact}>
				<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="gold" style={{ marginLeft: '8px', verticalAlign: 'middle' }} >
				  <path d="M12 .587l3.668 7.568L24 9.423l-6 5.885 1.417 8.255L12 18.897l-7.417 4.666L6 15.308 0 9.423l8.332-1.268z" />
				</svg>
				{previousScore + score}
			  </span>
			</h1>
		)}
	  { /* Основные блоки */ }
      {mode === 'sequential' ? (
        <div className={`${styles.sentenceContainer} ${ fadingSentences[currentSentenceIndex] ? styles.fading : '' }`} >
          <p className={styles.phrase}>{sentences[currentSentenceIndex]?.phrase}</p>
	      {errorMessages[currentSentenceIndex] && ( <p className={styles.error}>{errorMessages[currentSentenceIndex]}</p> )}
          {showCorrect[currentSentenceIndex] ? (
            <div className={styles.correctAnswer}><p> <span className={styles.checkmark}>✓</span> {sentences[currentSentenceIndex].translation}</p></div>
          ) : (
            <div className={styles.soloMode}>
              <div className={styles.slots}>
                {slots[currentSentenceIndex]?.map((word, slotIndex) => (
                  <div key={slotIndex}
                    ref={(el) => { slotsRef.current[currentSentenceIndex] = slotsRef.current[currentSentenceIndex] || []; 
								   slotsRef.current[currentSentenceIndex][slotIndex] = el;
                    }}
                    className={`${styles.slot} ${ hoveredSlot?.sentenceIndex === currentSentenceIndex && hoveredSlot?.slotIndex === slotIndex ? styles.over : ''}`}
                    onDrop={(e) => handleDrop(e, currentSentenceIndex, slotIndex)}
                    onDragOver={(e) => handleDragOver(e, currentSentenceIndex, slotIndex)}
                    onDragLeave={handleDragLeave}
                    onTouchEnd={handleTouchEnd}
                    onClick={() => word && handleSlotClick(currentSentenceIndex, slotIndex)}
                    data-sentence-index={currentSentenceIndex}
                    data-slot-index={slotIndex}
                  >
                    {word ? word.value : '\u00A0'}
                  </div>
                ))}
				  <ResetButton onClick={() => resetSentence(currentSentenceIndex)} disabled={isDraggingDisabled} title="Reset"/>
				  <HintButton onClick={() => hintWord(currentSentenceIndex)} disabled={isDraggingDisabled} title="Help" />

              </div>

              <div className={styles.words}>
                {availableWords.map((word, index) => (
                  <div key={word.id} ref={(el) => (wordsRef.current[index] = el)}
                    className={`${styles.word} ${ draggedWord?.id === word.id ? styles.dragging : '' } ${isDraggingDisabled ? styles.disabled : ''}`}
                    style={wordStyles[word.id] || {}}
                    draggable={!isDraggingDisabled}
                    onDragStart={(e) => handleDragStart(e, word)}
                    onDrag={(e) => handleDrag(e)}
                    onTouchStart={(e) => handleTouchStart(e, word)}
                    onTouchEnd={handleTouchEnd}
                  >
                    {word.value}
                  </div>
                ))}
                {draggedWord && (
                  <div className={`${styles.word} ${styles.dragging} ${styles.dragPreview}`}
                    style={{ 
					    left: dragPositionRef.current.x - 50, 
					    top: dragPositionRef.current.y - 20, 
                    }}
                  >
                    {draggedWord.value}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <>{/* Блок с несколькими предложениями!!! */}
		  
          {activeSentences.length === 0 && (
		      <div className={common.completed}>
            <p>{t.sentenceBlock.all_phrases_are_complete}</p>
            <button className={common.nextButton} onClick={() => onComplete(score)}>
              {t.sentenceBlock.next}
            </button>
          </div>
          )}

          {activeSentences.map((sentence) => (
            <div
              key={sentence.index}
              className={`${styles.sentenceContainer} ${
                fadingSentences[sentence.index] ? styles.fading : ''
              }`}
            >
              <p className={styles.phrase}>{sentence.phrase}</p>
              {errorMessages[sentence.index] && (
                <p className={styles.error}>{errorMessages[sentence.index]}</p>
              )}

              {showCorrect[sentence.index] ? (
                <div className={styles.correctAnswer}>
                  <p><span className={styles.checkmark}>✓</span> {sentence.translation}</p>
                </div>
              ) : (
                <>
                  <div className={styles.slots}>
                    {slots[sentence.index]?.map((word, slotIndex) => (
                      <div
                        key={slotIndex}
                        ref={(el) => {
                          slotsRef.current[sentence.index] =
                            slotsRef.current[sentence.index] || [];
                          slotsRef.current[sentence.index][slotIndex] = el;
                        }}
                        className={`${styles.slot} ${ hoveredSlot?.sentenceIndex === sentence.index && hoveredSlot?.slotIndex === slotIndex ? styles.over : '' }`}
                        onDrop={(e) => handleDrop(e, sentence.index, slotIndex)}
                        onDragOver={(e) => handleDragOver(e, sentence.index, slotIndex)}
                        onDragLeave={handleDragLeave}
                        onTouchEnd={handleTouchEnd}
                        onClick={() => word && handleSlotClick(sentence.index, slotIndex)}
                        data-sentence-index={sentence.index}
                        data-slot-index={slotIndex}
                      >
                        {word ? word.value : '\u00A0'}
                      </div>
                    ))}
				  <ResetButton onClick={() => resetSentence(sentence.index)} disabled={isDraggingDisabled} title="Reset"/> 	
				  <HintButton onClick={() => hintWord(sentence.index)} disabled={isDraggingDisabled} title="Help" />
                  </div>
				  
                </>
              )}
            </div>
          ))}

          <div className={styles.words}>
            {availableWords.map((word, index) => (
              <div
                key={word.id}
                ref={(el) => (wordsRef.current[index] = el)}
                className={`${styles.word} ${
                  draggedWord?.id === word.id ? styles.dragging : ''
                } ${isDraggingDisabled ? styles.disabled : ''}`}
                style={wordStyles[word.id] || {}}
                draggable={!isDraggingDisabled}
                onDragStart={(e) => handleDragStart(e, word)}
                onDrag={(e) => handleDrag(e)}
                onTouchStart={(e) => handleTouchStart(e, word)}
                onTouchEnd={handleTouchEnd}
              >
                {word.value}
              </div>
            ))}
            {draggedWord && (
              <div
                className={`${styles.word} ${styles.dragging} ${styles.dragPreview}`}
                style={{
                  left: dragPositionRef.current.x - 50,
                  top: dragPositionRef.current.y - 20,
                }}
              >
                {draggedWord.value}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SentenceBlock;


/*
	  <h1 className={styles.sentenceTitle}>
    
		{t.translateBlock.title.replace('{blockTitle}', block.blockTitle)} (
		  {mode === 'sequential' 
			? `${currentSentenceIndex + 1}/${sentences.length || 10}`
			: `${Math.min(completedSentences + 1, sentencesPerRound)}/${sentencesPerRound || 10}`}
		)
      </h1>		
      <p className={styles.score}>
		  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="gold" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
			<path d="M12 .587l3.668 7.568L24 9.423l-6 5.885 1.417 8.255L12 18.897l-7.417 4.666L6 15.308 0 9.423l8.332-1.268z" />
		  </svg>{previousScore + score}
	  </p>
*/
